import React, { useState, ChangeEvent, FormEvent } from 'react';
import Footer from '../Guest/sections/PageFooter';
import Banner from '../Guest/sections/PageBanner';
import PageHeader from '../Guest/sections/PageHeader';
import axios from 'axios';
import { REACT_APP_UPLOADS } from '../../constants';

// Types
type FormData = {
  fullName: string;
  phoneNumber: string;
  email: string;
  location: string;
  involvement: string;
  crimeType: string;
  otherCrime: string;
  agency: string[];
  description: string;
};

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
};

type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
};

// Reusable Components
const Input: React.FC<InputProps> = ({ label, ...props }) => (
  <div>
    <label className="block text-gray-700 font-medium">{label}</label>
    <input
      {...props}
      className="w-full mt-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
    />
  </div>
);

const Textarea: React.FC<TextareaProps> = ({ label, ...props }) => (
  <div>
    <label className="block text-gray-700 font-medium">{label}</label>
    <textarea
      {...props}
      className="w-full mt-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
    />
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-xl font-semibold text-green-600">{children}</h2>
);

// Main Component
const ReportIncident: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phoneNumber: '',
    email: '',
    location: '',
    involvement: '',
    crimeType: '',
    otherCrime: '',
    agency: [],
    description: '',
  });

  const handleChange = (
  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
) => {
  const { name, value, type } = e.target;

  if (type === 'checkbox') {
    const target = e.target as HTMLInputElement;
    const { checked } = target;

    setFormData((prev) => ({
      ...prev,
      agency: checked
        ? [...prev.agency, value]
        : prev.agency.filter((a) => a !== value),
    }));
  } else {
    setFormData({ ...formData, [name]: value });
  }
};


  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    axios.post(`${REACT_APP_UPLOADS}/api/v1/incident-reports/submit`, 
      [
        {
          "fullName": formData.fullName,
          "phoneNumber": formData.phoneNumber,
          "emailAddress": formData.email,
          "location": formData.location,
          "directlyInvolved": false,
          "crimeTypes":[formData.crimeType],
          "otherCrimeTypeDescription": formData.otherCrime,
          "agencies": formData.agency,
          "description": formData.description
        }
      ],
      {
                headers: {
                  "ngrok-skip-browser-warning": "true"
                }
              }
    ).then(function(res){
      if(res.status === 200){
            alert('Your report has been submitted. Thank you!');
            window.location.href = "/"
      }
    }).catch(function(err){
      console.log(err);
      alert('An issue occured while submitting the data. Please try again later!');
    })
  };

  const crimeOptions: string[] = [
    'Wildlife Crime',
    'Forestry Crime',
    'Pollution',
    'Land Degradation',
    'Marine Crime',
    'Other',
  ];

  const agencyOptions: string[] = [
    'NEMA',
    'KWS',
    'KFS',
    'Ministry of Environment and Climate Change',
    'Ministry of Mining, Blue Economy and Maritime Affairs',
    'County Government',
    'National Police Service',
  ];

  return (
    <>
      <Banner />
      <PageHeader />
      <section className='bg-green-100'>
      <div className="p-6 max-w-4xl mx-auto bg-white shadow-md rounded-md">
        <h1 className="text-2xl font-bold text-green-700 mb-4">Wildlife Crime Reporting</h1>
        <p className="text-gray-700 mb-6">
          The information you provide will be treated with confidentiality in accordance with the policies of the relevant Government Agency.
          Providing false information may result in legal action.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <SectionTitle>Contact Information</SectionTitle>
          <Input label="Full Name:" name="fullName" type="text" value={formData.fullName} onChange={handleChange} required />
          <Input label="Phone Number:" name="phoneNumber" type="tel" value={formData.phoneNumber} onChange={handleChange} required />
          <Input label="Email Address:" name="email" type="email" value={formData.email} onChange={handleChange} required />
          <Textarea label="Location of the incident (Be as specific as possible):" name="location" value={formData.location} onChange={handleChange} required />
          <Textarea label="Were you involved directly? Does it affect you, your family, and/or neighbourhood?" name="involvement" value={formData.involvement} onChange={handleChange} required />

          <SectionTitle>Type/Nature of Environmental Crime Observed</SectionTitle>
          <div className="space-y-2">
            {crimeOptions.map((crime) => (
              <label key={crime} className="block text-gray-700">
                <input
                  type="radio"
                  name="crimeType"
                  value={crime}
                  checked={formData.crimeType === crime}
                  onChange={handleChange}
                  required
                  className="mr-2"
                />
                {crime}
              </label>
            ))}
            {formData.crimeType === 'Other' && (
              <Input label="Please specify:" name="otherCrime" type="text" value={formData.otherCrime} onChange={handleChange} />
            )}
          </div>

          <SectionTitle>Agency Involved</SectionTitle>
          <div className="space-y-2">
            {agencyOptions.map((agency) => (
              <label key={agency} className="block text-gray-700">
                <input
                  type="checkbox"
                  name="agency"
                  value={agency}
                  checked={formData.agency.includes(agency)}
                  onChange={handleChange}
                  className="mr-2"
                />
                {agency}
              </label>
            ))}
          </div>

          <SectionTitle>Description of the Incident</SectionTitle>
          <Textarea
            label="Description (give as much detail as possible):"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
          />

          <button
            type="submit"
            className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Submit
          </button>
        </form>
      </div>
      </section>
      <Footer />
    </>
  );
};

export default ReportIncident;
